import React, { useEffect, useState } from "react";
import { TrendingUp, AlertTriangle } from "lucide-react";
import { LoadingBlock } from "@/components/ds";
import { api, type AttributionData } from "@/lib/api";

const CAT_LABEL: Record<string, string> = {
  resfil_sourced: "ResFil sourced", resfil_assisted: "ResFil assisted", resfil_recovered: "ResFil recovered",
  existing_customer: "Existing customer", unknown: "Unknown",
};
const CAT_COLOR: Record<string, string> = {
  resfil_sourced: "bg-blue-500", resfil_assisted: "bg-indigo-400", resfil_recovered: "bg-emerald-500",
  existing_customer: "bg-slate-400", unknown: "bg-slate-300",
};

export function AttributionPanel({ params }: { params: Record<string, string> }) {
  const [d, setD] = useState<AttributionData | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.attribution(params).then(setD).catch(() => setErr(true)); }, [JSON.stringify(params)]);
  if (err) return null;
  if (!d) return <LoadingBlock rows={3} />;

  const f = d.funnel;
  const maxPipe = Math.max(1, ...(d.attribution || []).map((a) => a.estimated_pipeline));
  const usd = (n: number) => `$${Math.round(n).toLocaleString()}`;

  return (
    <div className="space-y-4" data-testid="attribution-panel">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-slate-900">Revenue attribution</h2>
        <span className="text-xs text-slate-400">({d.plan} plan)</span>
      </div>

      {/* Funnel */}
      <div className="grid gap-3 sm:grid-cols-4" data-testid="attribution-funnel">
        {[["Leads", f.leads], ["Booked", f.booked], ["Completed jobs", f.completed]].map(([l, v]) => (
          <div key={l as string} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium text-slate-500">{l}</div>
            <div className="mt-1 font-display text-2xl font-semibold text-slate-900">{v as number}</div>
          </div>
        ))}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="attribution-revenue">
          <div className="text-xs font-medium text-slate-500">Actual revenue</div>
          {d.revenue_integrated
            ? <div className="mt-1 font-display text-2xl font-semibold text-emerald-700">{usd(d.actual_revenue ?? f.actual_revenue ?? 0)}</div>
            : <div className="mt-1 text-sm font-medium text-amber-600"><AlertTriangle className="mr-1 inline h-4 w-4" />Not integrated</div>}
        </div>
      </div>

      {d.revenue_note && <p className="text-xs text-amber-600" data-testid="attribution-revenue-note">{d.revenue_note}</p>}

      {d.basic_only && <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="attribution-upsell">You're seeing a basic summary. Upgrade to Growth for estimated opportunity analytics, or Pro for the full Revenue Attribution dashboard with service-level reporting.</p>}

      {/* Attribution by category (estimated pipeline — NOT actual revenue) */}
      {d.attribution && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="attribution-breakdown">
          <p className="mb-3 text-sm font-semibold text-slate-800">By attribution source <span className="font-normal text-slate-400">· estimated pipeline (not actual revenue)</span></p>
          <div className="space-y-2">
            {d.attribution.map((a) => (
              <div key={a.category} className="flex items-center gap-3" data-testid={`attribution-cat-${a.category}`}>
                <span className="w-32 shrink-0 text-xs text-slate-600">{CAT_LABEL[a.category]}</span>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
                  <div className={`h-full ${CAT_COLOR[a.category]}`} style={{ width: `${(a.estimated_pipeline / maxPipe) * 100}%` }} />
                </div>
                <span className="w-24 shrink-0 text-right text-xs text-slate-600">{a.leads} · {usd(a.estimated_pipeline)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service-level reporting */}
      {d.services && d.services.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="attribution-services">
          <p className="mb-2 text-sm font-semibold text-slate-800">Completed jobs by service</p>
          <div className="divide-y divide-slate-100">
            {d.services.map((s) => (
              <div key={s.service} className="flex items-center justify-between py-1.5 text-sm">
                <span className="text-slate-700">{s.service}</span>
                <span className="font-medium text-slate-900">{s.completed_jobs}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
